import React, { useState, useEffect, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  useTheme,
  TextField,
} from '@mui/material';
import axios from 'axios';
import {
  buildColorMapForNames,
  collectProfileNamesFromChartData,
} from '../utils/nameColors';
import {
  transformHistoryDataForChart,
  buildDashboardDateParams,
  formatDashboardRangeLabel,
  DASHBOARD_DATE_RANGE_OPTIONS,
} from '../utils/dashboardChartData';
import DashboardSummaryTab from '../components/dashboard/DashboardSummaryTab';
import PerformerChart from '../components/dashboard/PerformerChart';
import ResumePieChart from '../components/dashboard/ResumePieChart';
import CostsDashboard from '../components/CostsDashboard';
import JobLinkAnalyticsTab from '../components/dashboard/JobLinkAnalyticsTab';
import { useAuth } from '../context/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
);

const DATE_RANGE_OPTIONS = DASHBOARD_DATE_RANGE_OPTIONS;

const formatPerformerLabel = (user, currentUserId) => {
  if (user._id === currentUserId) {
    return 'My Performance';
  }
  if (user.role === 'bidder' && user.master?.name) {
    return `${user.name} (${user.master.name}'s Bidder)`;
  }
  if (user.role === 'bidder') {
    return `${user.name} (Bidder)`;
  }
  return user.name || user.email || 'Unknown';
};

const buildPerformersList = (currentUser, teamMembers) => {
  if (!currentUser) return [];

  const bidders = teamMembers
    .filter((member) => {
      if (member._id === currentUser._id || member.role !== 'bidder') {
        return false;
      }
      const masterId = member.master?._id || member.master;
      return masterId && masterId.toString() === currentUser._id.toString();
    })
    .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));

  return [currentUser, ...bidders];
};

const buildProfileColorMap = (bidDataByUser, callDataByUser, isDarkMode) => {
  const names = [
    ...collectProfileNamesFromChartData(bidDataByUser),
    ...collectProfileNamesFromChartData(callDataByUser),
  ];
  return buildColorMapForNames(names, isDarkMode);
};

const BidderPerformanceDashboard = () => {
  const theme = useTheme();
  const { user: authUser } = useAuth();
  const showLeadTabs = authUser?.role === 'lead' || authUser?.role === 'boss';
  const showCostsTab = showLeadTabs;
  const showJobLinkAnalyticsTab = showLeadTabs;
  const costsTabIndex = showJobLinkAnalyticsTab ? 4 : 3;
  const isDarkMode = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState(0);
  const [dateRange, setDateRange] = useState('14');
  const [fromDate, setFromDate] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [bidDataByUser, setBidDataByUser] = useState({});
  const [callDataByUser, setCallDataByUser] = useState({});
  const [callsByResume, setCallsByResume] = useState([]);
  const [profileColors, setProfileColors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const [userResponse, teamResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/auth/user`),
          axios.get(`${process.env.REACT_APP_API_URL}/teams/my-team`),
        ]);
        setCurrentUser(userResponse.data);
        setTeamMembers(teamResponse.data || []);
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load team data');
      }
    };

    loadTeam();
  }, []);

  const performers = useMemo(
    () => buildPerformersList(currentUser, teamMembers),
    [currentUser, teamMembers]
  );

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!performers.length) {
        if (currentUser) {
          setLoading(false);
        }
        return;
      }

      if ((dateRange === 'custom' || dateRange === 'single') && !fromDate) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const userIds = performers.map((performer) => performer._id).join(',');
        const dateParams = buildDashboardDateParams({ days: dateRange, fromDate });
        const [bidResponse, callResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/applications/bid-history-batch`, {
            params: {
              userIds,
              ...dateParams,
              excludeAutomatedEmails: 'true',
            },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/applications/call-history-batch`, {
            params: {
              userIds,
              ...dateParams,
            },
          }),
        ]);

        const nextBidData = bidResponse.data || {};
        const nextCallData = callResponse.data?.byUser || callResponse.data || {};
        const nextCallsByResume = callResponse.data?.callsByResume || [];
        setBidDataByUser(nextBidData);
        setCallDataByUser(nextCallData);
        setCallsByResume(nextCallsByResume);
        setProfileColors(buildProfileColorMap(nextBidData, nextCallData, isDarkMode));
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [performers, dateRange, fromDate, currentUser, isDarkMode]);

  const dateFilter = useMemo(
    () => ({
      days: dateRange,
      fromDate: (dateRange === 'custom' || dateRange === 'single') ? fromDate : '',
    }),
    [dateRange, fromDate]
  );

  const performerBidCharts = useMemo(
    () =>
      performers.map((performer) => ({
        id: performer._id,
        label: formatPerformerLabel(performer, currentUser?._id),
        chartData: transformHistoryDataForChart(
          bidDataByUser[performer._id],
          dateFilter,
          profileColors,
          isDarkMode
        ),
      })),
    [performers, bidDataByUser, dateFilter, profileColors, isDarkMode, currentUser?._id]
  );

  const performerCallCharts = useMemo(
    () =>
      performers.map((performer) => ({
        id: performer._id,
        label: formatPerformerLabel(performer, currentUser?._id),
        chartData: transformHistoryDataForChart(
          callDataByUser[performer._id],
          dateFilter,
          profileColors,
          isDarkMode
        ),
      })),
    [performers, callDataByUser, dateFilter, profileColors, isDarkMode, currentUser?._id]
  );

  const callsPieColorMap = useMemo(
    () => buildColorMapForNames(callsByResume.map((item) => item.resumeName), isDarkMode),
    [callsByResume, isDarkMode]
  );

  const callsTotal = useMemo(
    () => callsByResume.reduce((sum, item) => sum + item.count, 0),
    [callsByResume]
  );

  const selectedRangeLabel = formatDashboardRangeLabel(
    { days: dateRange, fromDate },
    DATE_RANGE_OPTIONS
  );

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    if (value !== 'custom' && value !== 'single') {
      setFromDate('');
    }
  };

  const handleFromDateChange = (value) => {
    setFromDate(value);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Dashboard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              label="Date Range"
              onChange={(e) => handleDateRangeChange(e.target.value)}
              disabled={loading && activeTab !== 0}
            >
              {DATE_RANGE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(dateRange === 'custom' || dateRange === 'single') && (
            <TextField
              label={dateRange === 'single' ? 'Date' : 'From Date'}
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => handleFromDateChange(e.target.value)}
              disabled={loading && activeTab !== 0}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 160 }}
            />
          )}
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Summary" />
        <Tab label="Bids" />
        <Tab label="Calls" />
        {showJobLinkAnalyticsTab && <Tab label="Job Links" />}
        {showCostsTab && <Tab label="Costs" />}
      </Tabs>

      {(dateRange === 'custom' || dateRange === 'single') && !fromDate && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Select a date to load dashboard data for the selected range.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {activeTab === 0 && (
        <DashboardSummaryTab
          performers={performers}
          dateRange={dateRange}
          fromDate={fromDate}
          selectedRangeLabel={selectedRangeLabel}
        />
      )}

      {activeTab !== 0 && activeTab < 3 && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : activeTab === 1 ? (
        performerBidCharts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No bid data available.</Typography>
          </Paper>
        ) : (
          performerBidCharts.map((performer) => (
            <PerformerChart
              key={performer.id}
              performer={performer}
              chartData={performer.chartData}
              rangeLabel={selectedRangeLabel}
              subtitle="Bid History by Resume (Stacked)"
              valueLabel="Number of Bids"
            />
          ))
        )
      ) : activeTab === 2 ? (
        <>
          <Box sx={{ maxWidth: 480, mx: { xs: 0, md: 'auto' }, width: '100%', mb: 3 }}>
            <ResumePieChart
              title={`Interview Invitations by Resume (${callsTotal} total)`}
              data={callsByResume}
              nameKey="resumeName"
              colorMap={callsPieColorMap}
              emptyMessage="No interview invitations in this period"
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Shared resumes are counted under the assigned bidder. Only Interview Invitation emails are included.
          </Typography>
          {performerCallCharts.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No call data available.</Typography>
            </Paper>
          ) : (
            performerCallCharts.map((performer) => (
              <PerformerChart
                key={performer.id}
                performer={performer}
                chartData={performer.chartData}
                rangeLabel={selectedRangeLabel}
                subtitle="Interview Invitations by Resume (Stacked)"
                valueLabel="Number of Calls"
              />
            ))
          )}
        </>
      ) : activeTab === 3 && showJobLinkAnalyticsTab ? (
        <JobLinkAnalyticsTab
          dateRange={dateRange}
          fromDate={fromDate}
          rangeLabel={selectedRangeLabel}
        />
      ) : activeTab === costsTabIndex && showCostsTab ? (
        <CostsDashboard
          dateRange={dateRange}
          fromDate={fromDate}
          rangeLabel={selectedRangeLabel}
        />
      ) : null}
    </Box>
  );
};

export default BidderPerformanceDashboard;
